import React from 'react'
import parse from 'html-react-parser'
import useShortCourses from './ShortCourseProvider'
import useTranslation from '../shared/TranslationProvider'
import { useIsDesktop } from '~/hooks/useMediaQuery'
import { cn } from '~/lib/utils'

const CardDiv = ({ divType, className, children }) => {
  if (divType === 'div') return <div className={className}>{children}</div>
  if (divType === 'p') return <p className={className}>{children}</p>
  if (divType === 'ul') return <ul className={className}>{children}</ul>
  if (divType === 'li') return <li className={className}>{children}</li>
}

const Sections = () => {
  const { sections, slug } = useShortCourses()
  const { t } = useTranslation(slug)
  const baseUrl = t('outline_item_path')
  const isDesktop = useIsDesktop()

  const workshopSections = sections.filter(
    (section) => section.slug !== 'summary',
  )

  return (
    <div className={cn('mx-4 max-w-7xl pt-6 md:mx-10', isDesktop && 'w-full')}>
      <ul>
        {workshopSections.map((section, idx) => {
          const { name, description } = section
          const sectionSlug = section.slug
          return (
            <li key={`${sectionSlug}-${idx}`}>
              <div className="flex text-lg font-medium hover:underline">
                <a className="text-black" href={`${baseUrl}/${sectionSlug}`}>
                  {name}
                </a>
                <span className="ml-1 flex items-center justify-center">
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M4.5 19.5L19.5 4.5M19.5 4.5H8.25M19.5 4.5V15.75"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </div>
              <ul className="my-4 pl-4">
                <li>{description}</li>
              </ul>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

const Card = () => {
  const { activeSubNav, slug, platformName } = useShortCourses()
  const { t } = useTranslation(slug)
  const cards = t('overview.subnav')
  const cardContents = cards.find(
    (card) => card.slug === activeSubNav,
  )?.card_content
  const isDesktop = useIsDesktop()

  const baseUrl = t('outline_item_path')

  if (!cardContents && baseUrl) return <Sections />

  const getSafeContent = (content) =>
    typeof content === 'string' ? content : ''

  const getParsedContent = (content, plugins, platformName) => {
    const safeContent = getSafeContent(content)
    const replacedContent = safeContent.replace(
      /\[PLATFORM_NAME\]/g,
      platformName,
    )
    return plugins && plugins.includes('parse-html')
      ? parse(replacedContent)
      : replacedContent
  }

  return (
    cardContents && (
      <div
        className={cn('mx-4 max-w-7xl pt-6 md:mx-10', isDesktop && 'w-full')}
      >
        {cardContents.map((cardContent, idx) => {
          const { div_type, className, content, children, plugins } =
            cardContent
          const parsedContent = getParsedContent(content, plugins, platformName)

          return (
            <CardDiv
              divType={div_type}
              className={className}
              key={`card-${idx}`}
            >
              {parsedContent}
              {children &&
                children.map((child, idx) => {
                  const { div_type, className, content, children, plugins } =
                    child
                  const parsedContent = getParsedContent(
                    content,
                    plugins,
                    platformName,
                  )

                  return (
                    <CardDiv
                      divType={div_type}
                      className={className}
                      key={`card-${idx}`}
                    >
                      {parsedContent}
                      {children &&
                        children.map((grandChild, idx) => {
                          const {
                            div_type,
                            className,
                            content,
                            children,
                            plugins,
                          } = grandChild
                          const parsedContent = getParsedContent(
                            content,
                            plugins,
                            platformName,
                          )

                          return (
                            <CardDiv
                              divType={div_type}
                              className={className}
                              key={`card-${idx}`}
                            >
                              {parsedContent}
                              {children &&
                                children.map((greatGrandChild, idx) => {
                                  const {
                                    div_type,
                                    className,
                                    plugins,
                                    content,
                                  } = greatGrandChild
                                  const parsedContent = getParsedContent(
                                    content,
                                    plugins,
                                    platformName,
                                  )

                                  return (
                                    <CardDiv
                                      divType={div_type}
                                      className={className}
                                      key={`card-${idx}`}
                                    >
                                      {parsedContent}
                                    </CardDiv>
                                  )
                                })}
                            </CardDiv>
                          )
                        })}
                    </CardDiv>
                  )
                })}
            </CardDiv>
          )
        })}
      </div>
    )
  )
}

export default Card
